import { useState, useContext } from "react";
import { AppContext } from "../../Context/AppContext";
import { Button, Icon } from "semantic-ui-react";
import * as XLSX from "xlsx";
import { useToasts } from "react-toast-notifications";
import moment from "moment";

const MAX_FILE_SIZE = 2 * 1024 * 1024;

export default function CMP_XLSXUPLOADER({
  set_mdl_open,
  api_path,
  var_modaltitle,
}) {
  /* VARIABLE DECLARATIONS
	---------------------------------------------------------------------------------*/
  const { userInfo, appVersion, isAppVersionOutdated } = useContext(AppContext);
  const [var_app_version] = appVersion;
  const [, set_is_app_version_outdated] = isAppVersionOutdated;
  const [xlsxData, setXlsxData] = useState([]);
  const [user] = userInfo;
  const [error, setError] = useState("");
  const [fileName, setFileName] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const { addToast } = useToasts();

  const validateFile = (file) => {
    if (!file.name.endsWith(".xlsx") && !file.name.endsWith(".xls")) {
      return "Only Excel (.xlsx or .xls) files are allowed.";
    }
    if (file.size > MAX_FILE_SIZE) {
      return "File size should be less than 2MB.";
    }
    return "";
  };
  /* EVENT FUNCTIONS
	=================================================================================*/
  const handleFileChange = (e) => {
    setError("");
    setXlsxData([]);
    setFileName("");
    setSelectedFile(null);

    const file = e.target.files[0];
    if (!file) return;

    const errorMessage = validateFile(file);
    if (errorMessage) {
      setError(errorMessage);
      return;
    }

    setFileName(file.name);
    setSelectedFile(file);

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const binaryStr = event.target.result;
        const workbook = XLSX.read(binaryStr, { type: "binary" });
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        const rawJsonData = XLSX.utils.sheet_to_json(worksheet, {
          defval: "",
          raw: true,
        });

        const jsonData = rawJsonData.map((row) => {
          const newRow = { ...row };
          for (const key in newRow) {
            const val = newRow[key];

            // Convert Excel serial numbers (plausible date range only)
            if (typeof val === "number" && val > 40000 && val < 60000) {
              const parsedDate = XLSX.SSF.parse_date_code(val);
              if (parsedDate) {
                const { y, m, d } = parsedDate;
                newRow[key] =
                  `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
              }
            }
          }
          return newRow;
        });

        if (jsonData.length === 0) {
          setError("Excel file is empty.");
          return;
        }

        setXlsxData(jsonData);
      } catch (err) {
        setError(err.message);
      }
    };
    reader.onerror = () => {
      setError("Error reading file.");
    };
    reader.readAsBinaryString(file);
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setError("Please select a file first.");
      return;
    }

    setUploading(true);
    setError("");

    try {
      const formData = new FormData();
      formData.append("document", selectedFile);
      const response = await fetch(api_path, {
        method: "POST",
        headers: {
          Authorization: "Token " + user.token,
          appversion: var_app_version,
          entityid: user.uuid,
        },
        body: formData,
      })
        .then((response) => response.json())
        .then((res) => {
          if (res.code === 200) {
            addToast(res.message, {
              appearance: "success",
              autoDismiss: true,
            });
            set_mdl_open(false); // Close modal on success
          } else {
            if (res?.version && res?.version !== var_app_version) {
              set_is_app_version_outdated(true);
            }
            addToast(res.message, {
              appearance: "error",
              autoDismiss: true,
            });
          }
        });
    } catch (err) {
      setError("Upload error: " + err.message);
    } finally {
      setUploading(false);
    }
  };

  const previewRows = xlsxData
    .slice(3, 10)
    .filter((row) =>
      Object.values(row).some(
        (val) => val !== "" && val !== null && val !== undefined,
      ),
    );

  const allKeys = Object.keys(previewRows[0] || {});

  // Find non-empty columns
  const nonEmptyKeys = allKeys.filter((key) =>
    previewRows.some(
      (row) => row[key] !== "" && row[key] !== null && row[key] !== undefined,
    ),
  );

  /* RENDER APP
	=================================================================================*/
  return (
    <>
      <div className="modal-header">
        <div className="modal-header-title">{var_modaltitle}</div>
        <div className="modal-header-close" onClick={() => set_mdl_open(false)}>
          <Icon name="times circle outline" />
        </div>
      </div>

      <div
        className="modal-content"
        style={{ maxHeight: "60vh", overflowY: "auto" }}
      >
        <div style={{ padding: "1rem", maxWidth: "600px" }}>
          <h4>Upload Excel File (.xlsx)</h4>
          <input type="file" accept=".xlsx,.xls" onChange={handleFileChange} />
          {fileName && (
            <p>
              <strong>Selected file:</strong> {fileName}
            </p>
          )}
          {error && <p style={{ color: "red" }}>{error}</p>}

          {xlsxData.length > 0 && (
            <div style={{ marginTop: "1rem" }}>
              <h4>Preview (First 5 Rows)</h4>
              <table
                border="1"
                cellPadding="5"
                style={{ width: "100%", tableLayout: "auto" }}
              >
                {/* <thead>
                  <tr>
                    {Object.keys(xlsxData[0]).map((header, i) => (
                      <th key={0}>{header[0]}</th>
                    ))}
                  </tr>
                </thead> */}
                <tbody>
                  {previewRows.map((row, i) => (
                    <tr key={i}>
                      {nonEmptyKeys.map((key, j) => {
                        let val = row[key];
                        let displayVal = val;

                        // Optional: if val is a date string like "6/11/2025"
                        if (
                          typeof val === "string" &&
                          /^\d{1,2}\/\d{1,2}\/\d{4}$/.test(val)
                        ) {
                          displayVal = moment(val, "M/D/YYYY").format(
                            "YYYY-MM-DD",
                          );
                        }

                        return <td key={j}>{displayVal}</td>;
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <div className="modal-footer">
        <Button type="button" color="grey" onClick={() => set_mdl_open(false)}>
          Close
        </Button>
        <Button
          type="button"
          color="blue"
          loading={uploading}
          disabled={uploading}
          onClick={handleUpload}
        >
          Upload
        </Button>
      </div>
    </>
  );
}
